import { queryOptions } from '@tanstack/react-query'
import { apiGet } from '@/shared/api'
import type { ReportRange } from '../model/range'
import {
  type CompanyReport,
  CompanyReportSchema,
  type EmployeeReport,
  EmployeeReportSchema,
  type ProjectReport,
  ProjectReportSchema,
} from '../model/schema'
import {
  CompanyReportResponseDto,
  EmployeeReportResponseDto,
  ProjectReportResponseDto,
} from './dto'
import { toCompanyRow, toEmployeeRow, toProjectRow, toSlice } from './mappers'

// Три среза одного отчёта: субъект строки — сотрудник, проект или компания.
export type ReportSliceName = 'employees' | 'projects' | 'companies'

export const REPORT_SLICE_PATH: Record<ReportSliceName, string> = {
  employees: '/reports/employees',
  projects: '/reports/projects',
  companies: '/reports/companies',
}

export const reportKeys = {
  all: ['reports'] as const,
  slice: (slice: ReportSliceName, range: ReportRange) =>
    [...reportKeys.all, slice, range.from, range.to] as const,
}

export function reportRangeQuery(range: ReportRange): Record<string, string> {
  return { from: range.from, to: range.to }
}

// Срез считается на бэке и переживает вкладку: staleTime держит переключение
// «сотрудники → проекты → сотрудники» без повторного запроса.
const STALE_TIME_MS = 60_000

export function employeeReportQueryOptions(range: ReportRange) {
  return queryOptions({
    queryKey: reportKeys.slice('employees', range),
    queryFn: async (): Promise<EmployeeReport> => {
      const body = await apiGet(
        REPORT_SLICE_PATH.employees,
        EmployeeReportResponseDto,
        reportRangeQuery(range),
      )
      return EmployeeReportSchema.parse(toSlice(body, toEmployeeRow))
    },
    staleTime: STALE_TIME_MS,
  })
}

export function projectReportQueryOptions(range: ReportRange) {
  return queryOptions({
    queryKey: reportKeys.slice('projects', range),
    queryFn: async (): Promise<ProjectReport> => {
      const body = await apiGet(
        REPORT_SLICE_PATH.projects,
        ProjectReportResponseDto,
        reportRangeQuery(range),
      )
      return ProjectReportSchema.parse(toSlice(body, toProjectRow))
    },
    staleTime: STALE_TIME_MS,
  })
}

export function companyReportQueryOptions(range: ReportRange) {
  return queryOptions({
    queryKey: reportKeys.slice('companies', range),
    queryFn: async (): Promise<CompanyReport> => {
      const body = await apiGet(
        REPORT_SLICE_PATH.companies,
        CompanyReportResponseDto,
        reportRangeQuery(range),
      )
      return CompanyReportSchema.parse(toSlice(body, toCompanyRow))
    },
    staleTime: STALE_TIME_MS,
  })
}
